import _classPrivateMethodInitSpec from "@babel/runtime/helpers/classPrivateMethodInitSpec";
import _classPrivateFieldInitSpec from "@babel/runtime/helpers/classPrivateFieldInitSpec";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _classPrivateFieldGet from "@babel/runtime/helpers/classPrivateFieldGet2";
import _assertClassBrand from "@babel/runtime/helpers/assertClassBrand";
import _classPrivateFieldSet from "@babel/runtime/helpers/classPrivateFieldSet2";
import { fetch } from "./fetch.js";
import { HttpAbortError, HttpFetchError, HttpParseError, HttpResponseError } from "./errors.js";
import InterceptorManager from "./InterceptorManager.js";
function isNil(value) {
  return value === undefined || value === null;
}
const base = document.querySelector("base");
const fullBaseHref = base ? base.href : location.origin + "/";
/**
 * Detect whether the input is a native browser abort error.
 *
 * @ref https://developer.mozilla.org/en-US/docs/Web/API/DOMException
 */
export const isHttpAbortError = error => error instanceof DOMException && error.code === 20;
const request = async (url, init, config) => {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve, reject) => {
    let response;
    config.url = url;
    let {
      responseType
    } = config.options || {};
    if (!responseType) {
      // Defaults to `json`
      responseType = "json";
    }
    try {
      response = await fetch(url, init);
    } catch (e) {
      reject(isHttpAbortError(e) ? new HttpAbortError(e.toString()) : new HttpFetchError(e.toString()));
      return;
    }
    if (!response.ok) {
      let responseJson;
      try {
        responseJson = await response.json();
      } catch {
        // Do nothing.
      }
      reject(new HttpResponseError(response, responseJson));
      return;
    }
    let data;
    try {
      data = await response[responseType]();
    } catch (e) {
      reject(isHttpAbortError(e) ? new HttpAbortError(e.toString()) : new HttpParseError(response));
      return;
    }
    const res = {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      data
    };
    resolve(res);
  });
};
export const getUrlWithParams = (url, params) => {
  if (params) {
    const parsedUrl = new URL(url, fullBaseHref);
    if (params instanceof URLSearchParams) {
      params.forEach(function (value, key) {
        parsedUrl.searchParams.append(key, value);
      });
    } else {
      Object.entries(params).forEach(_ref => {
        let [key, value] = _ref;
        if (Array.isArray(value)) {
          value.forEach(v => {
            parsedUrl.searchParams.append(key, isNil(v) ? "" : v);
          });
        } else {
          parsedUrl.searchParams.append(key, isNil(value) ? "" : value);
        }
      });
    }
    const {
      href
    } = parsedUrl;
    if (href.startsWith(fullBaseHref)) {
      return href.substr(fullBaseHref.length);
    }
    return href;
  }
  return url;
};
const getBodyAndHeaders = (data, headers) => {
  if (data !== undefined) {
    // `new Headers(undefined)` will throw a TypeError in older browsers (chrome 58).
    // https://bugs.chromium.org/p/chromium/issues/detail?id=335871
    const parsedHeaders = new Headers(headers || {});
    const contentTypeHeader = "Content-Type";
    let body = data;
    // If `Content-Type` already provided, ignore detecting content type.
    if (!parsedHeaders.has(contentTypeHeader)) {
      if (typeof data === "string") {
        parsedHeaders.set(contentTypeHeader, "application/x-www-form-urlencoded");
      } else if (data instanceof FormData) {
        // Do nothing
      } else {
        parsedHeaders.set(contentTypeHeader, "application/json");
        body = JSON.stringify(data);
      }
    }
    return {
      body,
      headers: parsedHeaders
    };
  }
  return {
    headers
  };
};
const simpleRequest = (method, url, config) => {
  const {
    params,
    responseType,
    interceptorParams,
    observe,
    noAbortOnRouteChange,
    useCache,
    ...requestInit
  } = config.options || {};
  return request(getUrlWithParams(url, params), {
    ...requestInit,
    method
  }, config);
};
const requestWithBody = (method, url, data, config) => {
  const {
    params,
    headers,
    responseType,
    interceptorParams,
    observe,
    noAbortOnRouteChange,
    useCache,
    ...requestInit
  } = config.options || {};
  return request(getUrlWithParams(url, params), {
    ...requestInit,
    method,
    ...getBodyAndHeaders(data, headers)
  }, config);
};
const defaultAdapter = config => {
  const {
    url,
    method,
    data
  } = config;

  // "DELETE", "GET", "HEAD" methods.
  if (["DELETE", "GET", "HEAD"].includes(config.method)) {
    return simpleRequest(method, url, config);
  }

  // "POST", "PUT" , "PATCH" methods.
  return requestWithBody(method, url, data, config);
};
var _adapter = /*#__PURE__*/new WeakMap();
var _Http_brand = /*#__PURE__*/new WeakSet();
class Http {
  constructor(_config) {
    var _this = this;
    _classPrivateMethodInitSpec(this, _Http_brand);
    _defineProperty(this, "interceptors", void 0);
    _classPrivateFieldInitSpec(this, _adapter, defaultAdapter);
    _defineProperty(this, "request", async (url, init, options) => {
      const {
        body,
        method,
        ...requestInit
      } = init || {};
      return _assertClassBrand(_Http_brand, this, _fetch).call(this, {
        url,
        data: body,
        method: method || "GET",
        options: {
          ...(options || {}),
          ...requestInit
        }
      });
    });
    _defineProperty(this, "simpleRequest", function (method, url) {
      let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
      return _assertClassBrand(_Http_brand, _this, _fetch).call(_this, {
        url,
        method,
        options
      });
    });
    _defineProperty(this, "requestWithBody", function (method, url, data) {
      let options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
      return _assertClassBrand(_Http_brand, _this, _fetch).call(_this, {
        url,
        method,
        data,
        options
      });
    });
    this.interceptors = {
      request: new InterceptorManager(),
      response: new InterceptorManager()
    };
    if (_config !== null && _config !== void 0 && _config.adapter) {
      _classPrivateFieldSet(_adapter, this, _config.adapter);
    }
  }
  getUrlWithParams(url, params) {
    return getUrlWithParams(url, params);
  }
  getBodyAndHeaders(data, headers) {
    return getBodyAndHeaders(data, headers);
  }
  get(url, options) {
    return _assertClassBrand(_Http_brand, this, _fetch).call(this, {
      url,
      method: "GET",
      options
    });
  }
  delete(url, options) {
    return _assertClassBrand(_Http_brand, this, _fetch).call(this, {
      url,
      method: "DELETE",
      options
    });
  }
  head(url, options) {
    return _assertClassBrand(_Http_brand, this, _fetch).call(this, {
      url,
      method: "HEAD",
      options
    });
  }
  post(url, data, options) {
    return _assertClassBrand(_Http_brand, this, _fetch).call(this, {
      url,
      method: "POST",
      data,
      options
    });
  }
  put(url, data, options) {
    return _assertClassBrand(_Http_brand, this, _fetch).call(this, {
      url,
      method: "PUT",
      data,
      options
    });
  }
  patch(url, data, options) {
    return _assertClassBrand(_Http_brand, this, _fetch).call(this, {
      url,
      method: "PATCH",
      data,
      options
    });
  }
}
function _fetch(config) {
  const chain = [];
  let promise = Promise.resolve(config);
  this.interceptors.request.forEach(interceptor => {
    chain.push(interceptor.fulfilled, interceptor.rejected);
  });
  chain.push(config => _classPrivateFieldGet(_adapter, this).call(this, config), undefined);
  this.interceptors.response.forEach(interceptor => {
    chain.push(res => {
      var _interceptor$fulfille;
      return (_interceptor$fulfille = interceptor.fulfilled) === null || _interceptor$fulfille === void 0 ? void 0 : _interceptor$fulfille.call(interceptor, res, config);
    }, error => {
      var _interceptor$rejected;
      return (_interceptor$rejected = interceptor.rejected) === null || _interceptor$rejected === void 0 ? void 0 : _interceptor$rejected.call(interceptor, error, config);
    });
  });
  chain.push(response => {
    var _config$options;
    return ((_config$options = config.options) === null || _config$options === void 0 ? void 0 : _config$options.observe) === "response" ? response : response.data;
  }, undefined);
  while (chain.length) {
    promise = promise.then(chain.shift(), chain.shift());
  }
  return promise;
}
let http = new Http();
function createHttpInstance(config) {
  http = new Http(config);
}
export { http, createHttpInstance, defaultAdapter };
//# sourceMappingURL=http.js.map